import { useState } from 'react';
import { Sparkles, Loader2, X } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { createPortal } from 'react-dom';
import { Textarea } from '@/components/ui/textarea';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { Editor } from '@tiptap/react';

interface AICustomPromptDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  editor: Editor;
}

export const AICustomPromptDialog = ({ open, onOpenChange, editor }: AICustomPromptDialogProps) => {
  const [customPrompt, setCustomPrompt] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const { toast } = useToast();

  const handleGenerate = async () => {
    if (!customPrompt.trim()) {
      toast({
        title: "Prompt required",
        description: "Please enter a custom prompt",
        variant: "destructive",
      });
      return;
    }

    setIsLoading(true);

    try {
      const { from, to } = editor.state.selection;
      const selectedText = editor.state.doc.textBetween(from, to, ' ');
      const fullText = editor.state.doc.textContent;

      const { data, error } = await supabase.functions.invoke('ai-text-tools', {
        body: {
          operation: 'custom',
          text: selectedText || fullText,
          customPrompt: customPrompt.trim(),
        },
      });

      if (error) throw error;

      if (data?.result) {
        if (selectedText) {
          editor.chain().focus().deleteSelection().insertContent(data.result).run();
        } else {
          editor.chain().focus().insertContent(data.result).run();
        }
        
        toast({
          title: "Generated successfully",
          description: "AI has processed your custom prompt",
        });
        
        setCustomPrompt('');
        onOpenChange(false);
      }
    } catch (error) {
      console.error('AI processing error:', error);
      toast({
        title: "AI processing failed",
        description: "Please try again",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
      e.preventDefault();
      handleGenerate();
    }
  };

  if (!open) return null;

  return createPortal(
    <AnimatePresence>
      {open && (
        <motion.div
          className="fixed inset-0 z-[10000] flex items-center justify-center"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.2 }}
          onClick={(e) => {
            if (e.target === e.currentTarget) {
              onOpenChange(false);
            }
          }}
        >
          {/* Backdrop */}
          <motion.div
            className="absolute inset-0 bg-black/60 backdrop-blur-sm"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          />

          {/* Dialog Card */}
          <motion.div
            className="relative rounded-[32px] overflow-hidden"
            style={{
              width: "360px",
              minHeight: "450px",
              backgroundColor: "#0e131f",
              boxShadow: "0 -10px 100px 10px rgba(78, 99, 255, 0.25), 0 0 10px 0 rgba(0, 0, 0, 0.5)",
            }}
            initial={{ scale: 0.9, opacity: 0, y: 20 }}
            animate={{ scale: 1, opacity: 1, y: 0 }}
            exit={{ scale: 0.9, opacity: 0, y: 20 }}
            transition={{ type: "spring", stiffness: 300, damping: 20 }}
          >
            {/* Close button */}
            <button
              onClick={() => onOpenChange(false)}
              className="absolute top-4 right-4 z-50 w-8 h-8 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center transition-colors"
              disabled={isLoading}
            >
              <X className="w-4 h-4 text-white/80" />
            </button>

            {/* Glass reflection overlay */}
            <div
              className="absolute inset-0 z-10 pointer-events-none"
              style={{
                background: "linear-gradient(135deg, rgba(255,255,255,0.08) 0%, rgba(255,255,255,0) 40%, rgba(255,255,255,0) 80%, rgba(255,255,255,0.05) 100%)",
                backdropFilter: "blur(2px)",
              }}
            />

            {/* Dark background gradient */}
            <div
              className="absolute inset-0 z-0"
              style={{
                background: "linear-gradient(180deg, #0e131f 0%, #000000 70%)",
              }}
            />

            {/* Noise texture overlay */}
            <div
              className="absolute inset-0 opacity-30 mix-blend-overlay z-5"
              style={{
                backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noise'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.65' numOctaves='5' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noise)'/%3E%3C/svg%3E")`,
              }}
            />

            {/* Purple/blue glow effects */}
            <div
              className="absolute bottom-0 left-0 right-0 h-2/3 z-10"
              style={{
                background: `
                  radial-gradient(ellipse at bottom right, rgba(172, 92, 255, 0.7) -10%, rgba(79, 70, 229, 0) 70%),
                  radial-gradient(ellipse at bottom left, rgba(56, 189, 248, 0.7) -10%, rgba(79, 70, 229, 0) 70%)
                `,
                filter: "blur(40px)",
              }}
            />

            {/* Central purple glow */}
            <div
              className="absolute bottom-0 left-0 right-0 h-2/3 z-15"
              style={{
                background: `radial-gradient(circle at bottom center, rgba(161, 58, 229, 0.7) -20%, rgba(79, 70, 229, 0) 60%)`,
                filter: "blur(45px)",
              }}
            />

            {/* Bottom border glow */}
            <div
              className="absolute bottom-0 left-0 right-0 h-[2px] z-25"
              style={{
                background: "linear-gradient(90deg, rgba(255, 255, 255, 0.05) 0%, rgba(255, 255, 255, 0.7) 50%, rgba(255, 255, 255, 0.05) 100%)",
                boxShadow: "0 0 15px 3px rgba(172, 92, 255, 0.8), 0 0 25px 5px rgba(138, 58, 185, 0.6), 0 0 35px 7px rgba(56, 189, 248, 0.4)",
              }}
            />

            {/* Content */}
            <div className="relative flex flex-col h-full p-8 z-40">
              {/* Icon circle */}
              <div
                className="w-12 h-12 rounded-full flex items-center justify-center mb-6"
                style={{
                  background: "linear-gradient(225deg, #171c2c 0%, #121624 100%)",
                  boxShadow: "0 6px 12px -2px rgba(0, 0, 0, 0.25), 0 3px 6px -1px rgba(0, 0, 0, 0.15), inset 1px 1px 3px rgba(255, 255, 255, 0.12), inset -2px -2px 4px rgba(0, 0, 0, 0.5)",
                }}
              >
                <Sparkles className="w-6 h-6 text-white" />
              </div>

              {/* Title and description */}
              <div className="mb-6">
                <h3 className="text-2xl font-medium text-white mb-3 leading-tight">
                  Custom AI Prompt
                </h3>
                <p className="text-sm text-gray-300 opacity-85 leading-relaxed">
                  Enter your custom instructions for the AI to process your text
                </p>
              </div>

              {/* Textarea */}
              <div className="mb-6 flex-1">
                <Textarea
                  value={customPrompt}
                  onChange={(e) => setCustomPrompt(e.target.value)}
                  onKeyDown={handleKeyDown}
                  placeholder="Example: Rewrite this in a more formal tone..."
                  className="min-h-[120px] bg-white/5 border-white/20 text-white/95 placeholder:text-white/40 focus:border-purple-400/60 focus:bg-white/10 focus:ring-2 focus:ring-purple-400/30 resize-none text-base rounded-2xl transition-all duration-200"
                  disabled={isLoading}
                  autoFocus
                />
                <p className="text-xs text-white/50 flex items-center gap-1.5 mt-2">
                  <span className="inline-block w-1 h-1 rounded-full bg-purple-400/60"></span>
                  Press {navigator.platform.includes('Mac') ? '⌘' : 'Ctrl'} + Enter to generate
                </p>
              </div>

              {/* Buttons */}
              <div className="flex flex-col gap-3">
                <motion.button
                  onClick={handleGenerate}
                  disabled={isLoading || !customPrompt.trim()}
                  className="w-full py-3 px-6 rounded-2xl bg-gradient-to-r from-purple-500 to-blue-500 text-white font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 transition-all duration-200 hover:shadow-lg hover:shadow-purple-500/25"
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  {isLoading ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      <span>Generating...</span>
                    </>
                  ) : (
                    <>
                      <Sparkles className="w-4 h-4" />
                      <span>Generate</span>
                    </>
                  )}
                </motion.button>
                
                <button
                  onClick={() => onOpenChange(false)}
                  disabled={isLoading}
                  className="w-full py-3 px-6 rounded-2xl bg-white/10 text-white/80 font-medium disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 hover:bg-white/15"
                >
                  Cancel
                </button>
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>,
    document.body
  );
};
